import { Injectable } from "@angular/core";
import { NgxIndexedDBService, WithID } from "ngx-indexed-db";
import {
  DecryptorPipe,
  encrypt,
  decrypt,
  decryptPermissions,
  EncryptorPipe,
} from "../../pipes/encryptors";
import { keyBy } from "lodash";
import { promise } from "protractor";
import { CryptoService } from "../encryption/crypto.service";
import { DataStore } from "aws-amplify";

@Injectable({
  providedIn: "root",
})
export class AcpIndexDBService {
  constructor(
    private dbService: NgxIndexedDBService,
    private cryptoService: CryptoService
  ) { }

  //Insert List
  public async processToIdbList(tableName, res, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }
    const dataPush: any[] = [];
    for (const entry of res) {
      const encryptedData = await encrypt(entry);
      dataPush.push(encryptedData);
    }
    const dataPass = {
      id: user_id,
      [column_name]: dataPush,
    };
    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedEntriesList(
    tableName: string,
    user_id,
    column_name
  ): Promise<any[]> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const userEntry = entries.find((entry) => entry.id === user_id);

      if (!userEntry) {
        return [];
      }

      const decryptedResults = await Promise.all(
        userEntry[column_name].map((entry) => decrypt(entry))
      );

      return decryptedResults;
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return [];
    }
  }

  //Insert an id and encrypted object, also delete only the user_id given
  public async processToIDB(
    tableName,
    res,
    encryptFields = [],
    user_id,
    column_name
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }

    const datapush = [];
    for (const item of res) {
      const dataToStore = {};
      for (const [key, value] of Object.entries(item)) {
        if (encryptFields.includes(key)) {
          dataToStore[key] =
            value !== null ? await encrypt(String(value)) : null;
        } else {
          dataToStore[key] = value;
        }
      }
      datapush.push(dataToStore);
    }

    const dataPass = {
      id: user_id,
      [column_name]: datapush,
    };
    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedEntries(
    tableName,
    decryptFields,
    user_id,
    column_name
  ): Promise<any[]> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const decryptFieldSet = new Set(decryptFields);

      const userEntriesMap = new Map(entries.map((entry) => [entry.id, entry]));
      const userEntry = userEntriesMap.get(user_id);
      if (!userEntry) {
        return [];
      }
      const decryptedResults = await Promise.all(
        userEntry[column_name].map(async (entry: any) => {
          const decryptedEntry: any = {};
          await Promise.all(
            Object.entries(entry).map(async ([key, value]) => {
              if (decryptFieldSet.has(key)) {
                decryptedEntry[key] =
                  value !== null ? await decrypt(value as ArrayBuffer) : null;
              }
            })
          );
          return decryptedEntry;
        })
      );
      return decryptedResults;
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return [];
    }
  }

  //Encrypt all
  public async processToIdbV2(tableName, res, encryptFields = []) {
    await this.dbService.clear(tableName).toPromise();
    for (const item of res) {
      const dataToStore = {};
      for (const [key, value] of Object.entries(item)) {
        if (encryptFields.includes(key)) {
          dataToStore[key] =
            value !== null ? await encrypt(String(value)) : null;
        }
      }
      try {
        const key = await this.dbService
          .add(tableName, dataToStore)
          .toPromise();
      } catch (error) {
        console.log(`Error adding item to ${tableName}:`, error);
      }
    }
  }

  //Decrypt All
  public async getDecryptedEntriesV2(
    tableName: string,
    decryptFields: string[]
  ): Promise<any[]> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();

      const decryptedResults = await Promise.all(
        entries.map(async (entry) => {
          const decryptedEntry: any = {};

          // Decrypt only the specified fields, but retain all fields
          await Promise.all(
            Object.entries(entry).map(async ([key, value]) => {
              if (decryptFields.includes(key)) {
                decryptedEntry[key] =
                  value !== null ? await decrypt(value as ArrayBuffer) : null;
              }
            })
          );

          return decryptedEntry;
        })
      );

      return decryptedResults;
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return [];
    }
  }

  //For single Objects
  public async processToIdbV3(tableName, res, encryptFields = []) {
    await this.dbService.clear(tableName).toPromise();

    const dataToStore = {};
    for (const [key, value] of Object.entries(res)) {
      if (encryptFields.includes(key)) {
        dataToStore[key] = value !== null ? await encrypt(String(value)) : null;
        // dataToStore[key] = value
      }
    }
    try {
      const key = await this.dbService.add(tableName, dataToStore).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedEntriesV3(
    tableName: string,
    decryptFields: string[]
  ): Promise<any> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      if (entries.length === 0) {
        return {};
      }

      const firstEntry = entries[0];
      const decryptedEntry = await Promise.all(
        Object.entries(firstEntry).map(async ([key, value]) => {
          if (decryptFields.includes(key)) {
            return [
              key,
              value !== null ? await decrypt(value as ArrayBuffer) : null,
            ];
          }
        })
      );

      return Object.fromEntries(decryptedEntry);
    } catch (error) {
      console.error(
        `Error fetching or decrypting entries from ${tableName}:`,
        error
      );
      return {};
    }
  }

  public async processToIdbPermissions(tableName, res, encryptFields = []) {
    await this.dbService.clear(tableName).toPromise();
    for (const item of res) {
      const dataToStore = {};
      for (const [key, value] of Object.entries(item)) {
        if (encryptFields.includes(key)) {
          if (
            value !== null &&
            typeof value === "object" &&
            !Array.isArray(value)
          ) {
            dataToStore[key] = {};
            for (const [nestedKey, nestedValue] of Object.entries(value)) {
              dataToStore[key][nestedKey] = await encrypt(String(nestedValue));
            }
          } else {
            dataToStore[key] = await encrypt(String(value));
          }
        }
      }
      try {
        const key = await this.dbService
          .add(tableName, dataToStore)
          .toPromise();
      } catch (error) {
        console.log(`Error adding item to ${tableName}:`, error);
      }
    }
  }

  public async processToIdbDoctorProducts(
    tableName,
    res,
    encryptFields = [],
    user_id,
    column_name
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }
    const datapush = [];
    for (const item of res) {
      const dataToStore = [];
      for (const [key, value] of Object.entries(item)) {
        if (encryptFields.includes(key)) {
          if (Array.isArray(value)) {
            dataToStore[key] = [];
            for (const [nestedKey, nestedValue] of Object.entries(value)) {
              if (
                nestedValue !== null &&
                typeof nestedValue === "object" &&
                !Array.isArray(nestedValue)
              ) {
                dataToStore[key][nestedKey] = {};
                for (const [lastKey, lastValue] of Object.entries(
                  nestedValue
                )) {
                  dataToStore[key][nestedKey][lastKey] = await encrypt(
                    String(lastValue)
                  );
                }
              }
            }
          } else {
            dataToStore[key] = value;
          }
        }
      }
      datapush.push(dataToStore);
    }
    const dataPass = {
      id: user_id,
      [column_name]: datapush,
    };
    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedEntriesDoctorProductsSingle(
    tableName,
    user_id,
    column_name,
    doctor_id
  ): Promise<any> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();

      const userEntriesMap = new Map(entries.map((entry) => [entry.id, entry]));
      const userEntry = userEntriesMap.get(user_id);
      if (!userEntry) {
        return {};
      }

      const doctorEntriesMap = new Map(
        userEntry[column_name].map((entry) => [entry.doctor_id, entry])
      );
      const doctorEntry = doctorEntriesMap.get(doctor_id);
      if (!doctorEntry) {
        return {};
      }

      const decryptedResults = await Promise.all(
        doctorEntry["output"].map(async (entry: any) => {
          const decryptedEntry: any = {};
          await Promise.all(
            Object.entries(entry).map(async ([key, value]) => {
              decryptedEntry[key] =
                value !== null ? await decrypt(value as ArrayBuffer) : null;
            })
          );
          return decryptedEntry;
        })
      );
      return decryptedResults;
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return {};
    }
  }

  public async getDecryptedEntriesDoctorProducts(
    tableName,
    user_id,
    decryptFields = [],
    column_name
  ): Promise<any> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const userEntriesMap = new Map(entries.map((entry) => [entry.id, entry]));
      const userEntry = userEntriesMap.get(user_id);
      if (!userEntry) {
        return {};
      }

      const dataToDecrypt = userEntry[column_name];
      const decryptedData = [];
      await Promise.all(
        dataToDecrypt.map(async (item) => {
          const decryptedItem = {};

          await Promise.all(
            Object.entries(item).map(async ([key, value]) => {
              if (decryptFields.includes(key)) {
                if (Array.isArray(value)) {
                  decryptedItem[key] = await Promise.all(
                    value.map(async (nestedValue) => {
                      if (
                        nestedValue !== null &&
                        typeof nestedValue === "object" &&
                        !Array.isArray(nestedValue)
                      ) {
                        const decryptedObj = {};
                        await Promise.all(
                          Object.entries(nestedValue).map(
                            async ([lastKey, lastValue]) => {
                              decryptedObj[lastKey] =
                                lastValue !== null
                                  ? await decrypt(lastValue as ArrayBuffer)
                                  : null;
                            }
                          )
                        );
                        return decryptedObj;
                      }
                      return nestedValue;
                    })
                  );
                } else {
                  decryptedItem[key] = value !== null ? value : null;
                }
              } else {
                decryptedItem[key] = value;
              }
            })
          );

          decryptedData.push(decryptedItem);
        })
      );

      return decryptedData;
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return {};
    }
  }

  public async getDecryptedEntriesPermissions(
    tableName: string,
    decryptFields: string[]
  ): Promise<any[]> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const decryptedResults = await Promise.all(
        entries.map(async (entry) => {
          const decryptedEntry: any = {};
          await Promise.all(
            Object.entries(entry).map(async ([key, value]) => {
              if (
                decryptFields.includes(key) &&
                value !== null &&
                typeof value === "object" &&
                !Array.isArray(value)
              ) {
                decryptedEntry[key] = await decryptPermissions(
                  value as ArrayBuffer
                );
                if (decryptedEntry[key] == "") {
                  decryptedEntry[key] = {};
                  await Promise.all(
                    Object.entries(value).map(
                      async ([nestedKey, nestedValue]) => {
                        decryptedEntry[key][nestedKey] =
                          nestedValue !== null
                            ? await decrypt(nestedValue as ArrayBuffer)
                            : null;
                      }
                    )
                  );
                }
              }
            })
          );
          return decryptedEntry;
        })
      );
      return decryptedResults;
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return [];
    }
  }

  public async processToIdbIncidental(
    tableName,
    res,
    encryptFields = [],
    user_id,
    column_name
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = await entries.find((entry) => entry.id === user_id);
    const datapush = [];
    const dataToStore = {};
    for (const [key, value] of Object.entries(res)) {
      if (encryptFields.includes(key)) {
        dataToStore[key] = value !== null ? await encrypt(String(value)) : null;
      } else {
        dataToStore[key] = value;
      }
    }
    datapush.push(dataToStore);
    const dataPass = {
      id: user_id,
      [column_name]: datapush,
    };

    try {
      if (existingEntry) {
        const existingData = existingEntry[column_name] || [];
        existingData.push(...datapush);
        const updatedEntry = {
          ...existingEntry,
          [column_name]: existingData,
        };
        await this.dbService.update(tableName, updatedEntry).toPromise();
      } else {
        await this.dbService.add(tableName, dataPass).toPromise();
      }
    } catch (error) {
      console.log(`Error adding/updating item in ${tableName}:`, error);
    }
  }

  public async processToIdbProducts(tableName, res, encryptFields = []) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    if (tableName == "acp_products") {
      const matchingEntries = entries.filter((entry) => {
        const doctorId = entry[0].doctor_id;
        const acpDate = entry[0].acp_date;
        return doctorId == res.doctor_id && acpDate == res.acp_date;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    }

    if (tableName == "acp_products_planned") {
      const matchingEntries = entries.filter((entry) => {
        const id = entry[0].id;
        const acpDate = entry[0].acp_date;
        return id == res.id && acpDate == res.acp_date;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    }

    const datapush = [];
    const dataObject = {};
    for (const [key, value] of Object.entries(res)) {
      if (encryptFields.includes(key)) {
        if (Array.isArray(value)) {
          dataObject[key] = [];
          for (const [nestedKey, nestedValue] of Object.entries(value)) {
            if (
              nestedValue !== null &&
              typeof nestedValue === "object" &&
              !Array.isArray(nestedValue)
            ) {
              dataObject[key][nestedKey] = {};
              for (const [lastKey, lastValue] of Object.entries(nestedValue)) {
                dataObject[key][nestedKey][lastKey] = await encrypt(
                  String(lastValue)
                );
              }
            }
          }
        } else {
          dataObject[key] = value;
        }
      }
    }
    datapush.push(dataObject);
    try {
      const key = await this.dbService.add(tableName, datapush).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedEntriesAllProductNotes(
    tableName,
    decryptFields
  ): Promise<any[]> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const decryptedData: any[] = [];

      // Decrypt data for all entries
      await Promise.all(
        entries.map(async (item) => {
          const decryptedItem: any = {};
          await Promise.all(
            Object.entries(item).map(async ([key, value]) => {
              if (
                value !== null &&
                typeof value === "object" &&
                !Array.isArray(value)
              ) {
                await Promise.all(
                  Object.entries(value).map(
                    async ([nestedKey, nestedValue]) => {
                      if (decryptFields.includes(nestedKey)) {
                        if (Array.isArray(nestedValue)) {
                          // Handle array of objects
                          decryptedItem[nestedKey] = await Promise.all(
                            nestedValue.map(async (lastItem) => {
                              if (
                                lastItem !== null &&
                                typeof lastItem === "object" &&
                                !Array.isArray(lastItem)
                              ) {
                                const decryptedLastItem: any = {};
                                await Promise.all(
                                  Object.entries(lastItem).map(
                                    async ([finalKey, finalValue]) => {
                                      decryptedLastItem[finalKey] =
                                        finalValue !== null
                                          ? await decrypt(
                                            finalValue as ArrayBuffer
                                          )
                                          : null;
                                    }
                                  )
                                );
                                return decryptedLastItem; // Return decrypted last item
                              }
                              return lastItem; // If not an object, return as is
                            })
                          );
                        } else {
                          // Handle single value
                          decryptedItem[nestedKey] = nestedValue;
                        }
                      }
                    }
                  )
                );
              }
            })
          );
          decryptedData.push(decryptedItem); // Push the decrypted item to the array
        })
      );

      return decryptedData; // Return the decrypted data
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return [];
    }
  }

  public async getDecryptedEntriesProductNotes(
    tableName,
    doctor_id,
    acp_date,
    decryptFields
  ): Promise<any> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      let matchingEntries;
      if (tableName == "acp_products" || tableName == "acp_notes") {
        matchingEntries = entries.filter((entry) => {
          const doctorId = entry[0].doctor_id;
          const acpDate = entry[0].acp_date;
          return doctorId == doctor_id && acpDate == acp_date;
        });
      } else {
        matchingEntries = entries.filter((entry) => {
          const doctorId = entry[0].id;
          const acpDate = entry[0].acp_date;
          return doctorId == doctor_id && acpDate == acp_date;
        });
      }

      if (!matchingEntries) {
        return {};
      }

      if (!matchingEntries[0]) {
        return {};
      }

      const decryptedData = [];
      await Promise.all(
        matchingEntries[0].map(async (item) => {
          const decryptedItem = {};
          await Promise.all(
            Object.entries(item).map(async ([key, value]) => {
              if (decryptFields.includes(key)) {
                if (Array.isArray(value)) {
                  decryptedItem[key] = await Promise.all(
                    value.map(async (nestedValue) => {
                      if (
                        nestedValue !== null &&
                        typeof nestedValue === "object" &&
                        !Array.isArray(nestedValue)
                      ) {
                        const decryptedObj = {};
                        await Promise.all(
                          Object.entries(nestedValue).map(
                            async ([lastKey, lastValue]) => {
                              decryptedObj[lastKey] =
                                lastValue !== null
                                  ? await decrypt(lastValue as ArrayBuffer)
                                  : null;
                            }
                          )
                        );
                        return decryptedObj;
                      }
                      return nestedValue;
                    })
                  );
                } else {
                  decryptedItem[key] = value;
                }
              }
            })
          );
          decryptedData.push(decryptedItem);
        })
      );
      return decryptedData;
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return {};
    }
  }

  public async processToIdbNotes(tableName, res, encryptFields = []) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    if (tableName == "acp_notes") {
      const matchingEntries = entries.filter((entry) => {
        const doctorId = entry[0].doctor_id;
        const acpDate = entry[0].acp_date;
        return doctorId == res.doctor_id && acpDate == res.acp_date;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    } else {
      const matchingEntries = entries.filter((entry) => {
        const id = entry[0].id;
        const acpDate = entry[0].acp_date;
        return id == res.id && acpDate == res.acp_date;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    }

    const datapush = [];
    const dataObject = {};
    for (const [key, value] of Object.entries(res)) {
      if (encryptFields.includes(key)) {
        if (Array.isArray(value)) {
          dataObject[key] = [];
          for (const [nestedKey, nestedValue] of Object.entries(value)) {
            if (
              nestedValue !== null &&
              typeof nestedValue === "object" &&
              !Array.isArray(nestedValue)
            ) {
              dataObject[key][nestedKey] = {};
              for (const [lastKey, lastValue] of Object.entries(nestedValue)) {
                dataObject[key][nestedKey][lastKey] = await encrypt(
                  String(lastValue)
                );
              }
            }
          }
        } else {
          dataObject[key] = value;
        }
      }
    }
    datapush.push(dataObject);
    try {
      const key = await this.dbService.add(tableName, datapush).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async saveToIDB(
    tableName: string,
    newData: any,
    user_id: number,
    column_name: string
  ) {
    try {
      const datapush = [];
      for (const item of newData) {
        try {
          const dataToStore: any = {};
          const itemImage = item.imageFile;
          const formImages = item.formImages || [];
          const trylang = JSON.stringify(item);
          dataToStore["data"] = await this.cryptoService.encryptDataBased(trylang);

          // Convert image to base64 if valid
          if (itemImage && itemImage.name && itemImage.type) {
            try {
              dataToStore["imageFile"] = {
                name: itemImage.name,
                base64: await this.convertBlobToBase64(itemImage),
                fileType: itemImage.type,
              };
            } catch (imgErr) {
              console.warn("Error converting itemImage to base64", imgErr);
            }
          } else {
            console.warn("Missing or invalid imageFile in item:", item);
          }

          if (formImages.length > 0) {
            dataToStore["formImages"] = [];
            for (const formImage of formImages) {
              const formImageData = {
                key: formImage.key,
                file: formImage.file,
                metadata: formImage.metadata,
                formCode: formImage.formCode,
              };
              dataToStore["formImages"].push(formImageData);
            }
          }

          datapush.push(dataToStore);
        } catch (itemErr) {
          console.error("Failed to process item:", item, itemErr);
        }
      }

      if (datapush.length === 0) {
        console.warn("No valid data to store for user_id:", user_id);
        return;
      }

      const dataPass = {
        id: user_id,
        [column_name]: datapush,
      };


      const existing = await this.dbService.getByKey(tableName, user_id).toPromise();
      if (existing) {
        await this.dbService.update(tableName, dataPass).toPromise();
      } else {
        await this.dbService.add(tableName, dataPass).toPromise();
      }
      console.log("Data successfully saved to IDB");
    } catch (error) {
      console.error("Failed to save to IDB:", error);
    }
  }


  // public async saveToIDB(
  //   tableName: string,
  //   newData: any,
  //   user_id: number,
  //   column_name
  // ) {
  //   const entries: any[] = await this.dbService.getAll(tableName).toPromise();
  //   const existingEntry = entries.find((entry) => entry.id === user_id);
  //   if (existingEntry) {
  //     await this.dbService.delete(tableName, user_id).toPromise();
  //   }

  //   const datapush = [];
  //   for (const item of newData) {
  //     const dataToStore = {};
  //     const itemImage = item.imageFile;
  //     const formImages = item.formImages || []; 
  //     const trylang = JSON.stringify(item);
  //     dataToStore["data"] = await this.cryptoService.encryptDataBased(trylang);


  //     //handles empty image
  //     if (itemImage && itemImage.name && itemImage.type) {
  //       try {
  //         dataToStore["imageFile"] = {
  //           name: itemImage.name,
  //           base64: await this.convertBlobToBase64(itemImage),
  //           fileType: itemImage.type
  //         };
  //       } catch (imgErr) {
  //         console.warn("Error converting itemImage to base64", imgErr);
  //       }
  //     } else {
  //       console.warn("Missing or invalid imageFile in item:", item);
  //     }

  //     if (formImages.length > 0) {
  //       dataToStore["formImages"] = [];
  //       for (const formImage of formImages) {
  //         const formImageData = {
  //           key: formImage.key,
  //           file: formImage.file,
  //           metadata: formImage.metadata,
  //           formCode: formImage.formCode
  //         };
  //         dataToStore["formImages"].push(formImageData);
  //       }
  //     }

  //     datapush.push(dataToStore);
  //   }

  //   const dataPass = {
  //     id: user_id,
  //     [column_name]: datapush,
  //   };

  //   try {
  //     const key = await this.dbService.add(tableName, dataPass).toPromise();
  //   } catch (error) {
  //     console.log(`Error adding item to ${tableName}:`, error);
  //   }
  // }



  public async loadFromIDB(
    tableName: string,
    user_id: number,
    column_name: string
  ) {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const existingEntry = entries.find((entry) => entry.id === user_id);

      if (!existingEntry || !existingEntry[column_name]) {
        return [];
      }
      const decryptedData = [];
      for (const item of existingEntry[column_name]) {
        const itemToReturn = {};
        for (const [key, value] of Object.entries(item)) {
          if (key == "imageFile") {
            const imageValue = value as { base64: string; name: string; fileType: string };
            itemToReturn["imageFile"] = this.base64ToFile(imageValue.base64, imageValue.name, imageValue.fileType);
          } else if (key === "formImages") {
            itemToReturn["formImages"] = (value as any[]).map((formImage: any) => {
              if (typeof formImage.file === "string" && formImage.file.startsWith("data:")) {
                const reconstructedFile = this.base64ToFile(
                  formImage.file,
                  formImage.metadata?.fileName || "formImage.png",
                  formImage.metadata?.type || "image/png"
                );
                return { ...formImage, file: reconstructedFile, formCode: formImage.formCode };
              }
              return {
                ...formImage,
                formCode: formImage.formCode,
              };
            });
          } else if (key == "data") {
            const jsonParse = JSON.parse(
              await this.cryptoService.decryptDataBased(String(value))
            );
            for (const [jsonKey, jsonValue] of Object.entries(jsonParse)) {
              itemToReturn[jsonKey] = jsonValue;
            }
          }
        }
        decryptedData.push(itemToReturn);
      }
      return decryptedData;
    } catch (error) {
      console.log(`Error loading data from ${tableName}:`, error);
      return null;
    }
  }

  public async saveToIDBActivity(
    tableName: string,
    newData: any[],
    user_id: number,
    column_name
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.id === user_id);
    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }

    const datapush = [];
    for (const item of newData) {
      const dataToStore = {};
      const itemImage = item.imageFile;
      const formImages = item.formImages || [];
      const trylang = JSON.stringify(item);
      dataToStore["data"] = await this.cryptoService.encryptDataBased(trylang);
      if (itemImage && itemImage.name && itemImage.type) {
        try {
          dataToStore["imageFile"] = {
            name: itemImage.name,
            base64: await this.convertBlobToBase64(itemImage),
            fileType: itemImage.type
          };
        } catch (imgErr) {
          console.warn("Error converting itemImage to base64", imgErr);
        }
      } else {
        console.warn("Missing or invalid imageFile in item:", item);
      }



      if (formImages.length > 0) {
        dataToStore["formImages"] = [];
        for (const formImage of formImages) {
          const formImageData = {
            key: formImage.key,
            file: formImage.file,
            metadata: formImage.metadata,
            formCode: formImage.formCode
          };
          dataToStore["formImages"].push(formImageData);
        }
      }

      datapush.push(dataToStore);
    }

    const dataPass = {
      id: user_id,
      [column_name]: datapush,
    };

    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async loadFromIDBActivity(
    tableName: string,
    user_id: number,
    column_name: string
  ) {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const existingEntry = entries.find((entry) => entry.id === user_id);

      if (!existingEntry || !existingEntry[column_name]) {
        return [];
      }
      const decryptedData = [];
      for (const item of existingEntry[column_name]) {
        const itemToReturn = {};
        for (const [key, value] of Object.entries(item)) {
          if (key == "imageFile") {
            const imageValue = value as { base64: string; name: string; fileType: string };
            itemToReturn["imageFile"] = this.base64ToFile(imageValue.base64, imageValue.name, imageValue.fileType);
          } else if (key === "formImages") {
            itemToReturn["formImages"] = (value as any[]).map((formImage: any) => {
              if (typeof formImage.file === "string" && formImage.file.startsWith("data:")) {
                const reconstructedFile = this.base64ToFile(
                  formImage.file,
                  formImage.metadata?.fileName || "formImage.png",
                  formImage.metadata?.type || "image/png"
                );
                return { ...formImage, file: reconstructedFile, formCode: formImage.formCode };
              }
              return {
                ...formImage,
                formCode: formImage.formCode,
              };
            });
          } else if (key == "data") {
            const jsonParse = JSON.parse(
              await this.cryptoService.decryptDataBased(String(value))
            );
            for (const [jsonKey, jsonValue] of Object.entries(jsonParse)) {
              itemToReturn[jsonKey] = jsonValue;
            }
          }
        }
        decryptedData.push(itemToReturn);
      }
      return decryptedData;
    } catch (error) {
      console.log(`Error loading data from ${tableName}:`, error);
      return null;
    }
  }

  // Helper method to convert base64 string to File
  private base64ToFile(base64: string, fileName: string, fileType: string): File {
    const byteString = atob(base64.split(",")[1]);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }
    return new File([byteArray], fileName, { type: fileType });
  }

  public async deleteAcpProductNotes(tableName, doctor_id, acp_date) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    if (tableName == "acp_products" || tableName == "acp_notes") {
      const matchingEntries = entries.filter((entry) => {
        const doctorId = entry[0].doctor_id;
        const acpDate = entry[0].acp_date;
        return doctorId == doctor_id && acpDate == acp_date;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    } else {
      const matchingEntries = entries.filter((entry) => {
        const id = entry[0].id;
        const acpDate = entry[0].acp_date;
        return id == doctor_id && acpDate == acp_date;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    }
  }

  public async deleteAcpProductNotesId(tableName, doctor_id) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    if (tableName == "acp_products" || tableName == "acp_notes") {
      const matchingEntries = entries.filter((entry) => {
        const doctorId = entry[0].doctor_id;
        return doctorId == doctor_id;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    } else {
      const matchingEntries = entries.filter((entry) => {
        const id = entry[0].id;
        return id == doctor_id;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    }
  }

  public async deleteActivityDetailbyId(tableName, act_id, acp_date) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const matchingEntries = entries.filter((entry) => {
      const id = entry[0].id;
      const acpDate = entry[0].acp_date;
      return id == act_id && acpDate == acp_date;
    });
    for (const entry of matchingEntries) {
      const entryId = entry.id;
      await this.dbService.delete(tableName, entryId).toPromise();
    }
  }

  public async processToIdbActivityDetail(tableName, res, encryptFields = []) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    const matchingEntries = entries.filter((entry) => {
      const id = entry[0].id;
      const acpDate = entry[0].acp_date;
      return id == res.id && acpDate == res.acp_date;
    });
    for (const entry of matchingEntries) {
      const entryId = entry.id;
      await this.dbService.delete(tableName, entryId).toPromise();
    }

    const datapush = [];
    const dataObject = {};
    for (const [key, value] of Object.entries(res)) {
      if (encryptFields.includes(key)) {
        dataObject[key] = await encrypt(String(value));
      } else {
        dataObject[key] = value;
      }
    }
    datapush.push(dataObject);

    try {
      const key = await this.dbService.add(tableName, datapush).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedEntriesActivityDetail(
    tableName,
    decryptFields
  ): Promise<any> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const decryptedData: any[] = [];

      // Decrypt data for all entries
      await Promise.all(
        entries.map(async (item) => {
          const decryptedItem: any = {};
          await Promise.all(
            Object.entries(item).map(async ([key, value]) => {
              if (
                value !== null &&
                typeof value === "object" &&
                !Array.isArray(value)
              ) {
                await Promise.all(
                  Object.entries(value).map(
                    async ([nestedKey, nestedValue]) => {
                      if (decryptFields.includes(nestedKey)) {
                        decryptedItem[nestedKey] = await decrypt(
                          nestedValue as ArrayBuffer
                        );
                      } else {
                        decryptedItem[nestedKey] = nestedValue;
                      }
                    }
                  )
                );
              }
            })
          );
          decryptedData.push(decryptedItem);
        })
      );

      return decryptedData;
    } catch (error) {
      console.error(
        `Error fetching decrypted entries from ${tableName}:`,
        error
      );
      return [];
    }
  }

  public async deleteActivityDetail(tableName, res) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const matchingEntries = entries.filter((entry) => {
      const id = entry[0].id;
      const acpDate = entry[0].acp_date;
      return id == res.id && acpDate == res.acp_date;
    });
    for (const entry of matchingEntries) {
      const entryId = entry.id;
      await this.dbService.delete(tableName, entryId).toPromise();
    }
  }

  // THIS ARE THE AES ENCRYPTIONS!

  public async processAESToIDB(tableName, res, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }

    if (res.length == 0) {
      return [];
    }

    // specil case for acp_call_info to handle form/survey images
    if (tableName === "acp_call_info") {
      for (const callInfo of res) {
        if (callInfo.surveys && callInfo.surveys.length > 0) {
          for (const survey of callInfo.surveys) {
            if (survey.formImages) {
              for (const key in survey.formImages) {
                const formImage = survey.formImages[key];
                const imageUrl = formImage?.url || formImage?.value.url;

                if (imageUrl) {
                  try {
                    const imageBlob = await this.fetchImageFromS3(imageUrl);
                    const base64Image = await this.convertBlobToBase64(imageBlob);

                    survey.formImages[key] = {
                      ...formImage,
                      base64: base64Image,
                      fileName: formImage.fileName || imageUrl.split('/').pop(),
                      metadata: formImage.metadata || {},
                      type: formImage.type || "UNKNOWN",
                    };
                  } catch (error) {
                    console.error(`Failed to fetch or process image for key: ${key}`, error);
                  }
                }
              }
            }
          }
        }
      }
    }

    const testTO = JSON.stringify(res);
    const testEncrypted = await this.cryptoService.encryptDataBased(testTO);
    const dataPass = {
      id: user_id,
      [column_name]: testEncrypted,
    };
    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedAesEntries(tableName, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const userEntriesMap = new Map(entries.map((entry) => [entry.id, entry]));
    const userEntry = userEntriesMap.get(user_id);

    if (!userEntry || userEntry == undefined) {
      return [];
    }

    //special case lng to sa acp_products_planned para di na magclear data sa 10.0.1
    if (tableName === 'acp_products_planned') {
      if (userEntry['acp_product_planned_list']) {
        column_name = 'acp_product_planned_list';
      } else if (userEntry['acp_products_planned_list']) {
        column_name = 'acp_products_planned_list';
      } else {
        return [];
      }
    }

    if (!userEntry[column_name] || userEntry[column_name].length <= 0) {
      return [];
    }

    const decryptedData = await this.cryptoService.decryptDataBased(
      userEntry[column_name]
    );
    // return JSON.parse(decryptedData);

    const parsedData = JSON.parse(decryptedData);

    // special case for acp_call_info to handle form images
    if (tableName === "acp_call_info") {
      for (const callInfo of parsedData) {
        if (callInfo.surveys && callInfo.surveys.length > 0) {
          for (const survey of callInfo.surveys) {
            if (survey.formImages) {
              const formattedFormImages = new Map();

              for (const key in survey.formImages) {
                const formImage = survey.formImages[key];
                const base64Image = formImage.base64;
                const imageUrl = formImage.url;

                if (base64Image) {
                  const fullFileName = formImage.fileName || `file-${key}`;
                  const fileName = fullFileName.split('?')[0];
                  const mimeType = base64Image.match(/data:(.*?);base64,/)?.[1] || "application/octet-stream";
                  const metadata = formImage.metadata || { fieldId: key };
                  const imageBlob = await this.convertBase64ToBlob(base64Image);

                  formattedFormImages.set(key, {
                    file: new File([imageBlob], fileName, { type: mimeType }),
                    fileName: fileName,
                    metadata: metadata,
                    type: mimeType,
                    url: imageUrl,
                  });
                }
              }
              survey.formImages = formattedFormImages;
            }
          }
        }
      }
    }
    return parsedData;
  }

  // helper to fetch image/s from S3 - for form images
  private async fetchImageFromS3(url: string): Promise<Blob> {
    const response = await fetch(url);
    return await response.blob();
  }

  // helper to convert blob to a base64 string - for form images
  private async convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // helper to convert base64 to blob - for form images
  private async convertBase64ToBlob(base64: string): Promise<Blob> {
    const mimeTypeMatch = base64.match(/data:(.*?);base64,/);
    const mimeType = mimeTypeMatch ? mimeTypeMatch[1] : "application/octet-stream";
    const base64Data = base64.replace(/^data:(.*?);base64,/, "");
    const binaryData = atob(base64Data);
    const byteArray = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      byteArray[i] = binaryData.charCodeAt(i);
    }
    return new Blob([byteArray], { type: mimeType });
  }

  public async processAESToIDBPostedForms(tableName, res, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }

    if (res.length === 0) {
      return [];
    }

    for (const form of res) {
      if (form.fields) {
        for (const field of form.fields) {
          if (field.field_type_code.toUpperCase() === 'IMG' && field.answer) {
            try {
              const imageBlob = await this.fetchImageFromS3(field.answer);
              const compressedImage = await this.compressImage(imageBlob, 0.5);
              const base64Image = await this.convertBlobToBase64(compressedImage);

              field.answer = base64Image;
            } catch (error) {
              console.error(`Failed to fetch or compress image for field: ${field.field_id}`, error);
            }
          }
        }
      }
    }

    const encryptedData = await this.cryptoService.encryptDataBased(JSON.stringify(res));
    const dataToSave = {
      id: user_id,
      [column_name]: encryptedData,
    };

    try {
      await this.dbService.add(tableName, dataToSave).toPromise();
    } catch (error) {
      console.error(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedPostedFormsEntries(tableName, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const userEntry = entries.find((entry) => entry.id === user_id);

    if (!userEntry || !userEntry[column_name]) {
      return [];
    }

    const decryptedData = await this.cryptoService.decryptDataBased(userEntry[column_name]);
    const parsedData = JSON.parse(decryptedData);

    for (const form of parsedData) {
      if (form.fields) {
        for (const field of form.fields) {
          if (field.field_type_code.toUpperCase() === 'IMG' && field.answer) {
            field.answer = field.answer;
          }
        }
      }
    }

    return parsedData;
  }

  // compress image to reduce its size in main form module
  private async compressImage(imageBlob: Blob, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const reader = new FileReader();
      reader.onload = () => {
        img.src = reader.result as string;
      };
      reader.onerror = (error) => reject(error);

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0, img.width, img.height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else { reject(new Error('Failed to compress image')); }
          }, 'image/png', quality
        );
      };

      reader.readAsDataURL(imageBlob);
    });
  }




  public async getDecryptedAesEntriesAppUser(tableName) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    if (!entries || entries.length === 0) {
      return [];
    }

    const decryptedData = await this.cryptoService.decryptDataBased(
      entries[0].user
    );
    return JSON.parse(decryptedData);
  }





  public async processAEStoIDBwithCustomer(
    tableName,
    res,
    encryptFields = [],
    user_id,
    customer_id
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    const matchingEntries = entries.filter((entry) => {
      const id = entry[0].id;
      const customerId = entry[0].customer_id;
      return id == user_id && customerId == customer_id;
    });
    for (const entry of matchingEntries) {
      const entryId = entry.id;
      await this.dbService.delete(tableName, entryId).toPromise();
    }

    const datapush = [];
    const dataObject = {};
    for (const [key, value] of Object.entries(res)) {
      if (encryptFields.includes(key)) {
        if (Array.isArray(value)) {
          dataObject[key] = [];
          for (const [nestedKey, nestedValue] of Object.entries(value)) {
            if (
              nestedValue !== null &&
              typeof nestedValue === "object" &&
              !Array.isArray(nestedValue)
            ) {
              dataObject[key][nestedKey] = {};
              for (const [lastKey, lastValue] of Object.entries(nestedValue)) {
                dataObject[key][nestedKey][lastKey] = await encrypt(
                  String(lastValue)
                );
              }
            }
          }
        } else {
          dataObject[key] = value;
        }
      }
    }
    datapush.push(dataObject);
    try {
      const key = await this.dbService.add(tableName, datapush).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  // public async processAEStoIDBwithCustomer1(tableName, res, user_id, column_name, customer_id) {
  //   const entries: any[] = await this.dbService.getAll(tableName).toPromise();
  //   const existingEntry = entries.find(entry => entry.id === user_id && entry.customer_id === customer_id);

  //   if (existingEntry) {
  //     await this.dbService.delete(tableName, user_id).toPromise();
  //   }

  //   if (res.length == 0) {
  //     return []
  //   }

  //   const testTO = JSON.stringify(res);
  //   // const testEncrypted = await encrypt(String(res))
  //   const testEncrypted = await this.cryptoService.encryptDataBased(testTO);
  //   const dataPass = {
  //     id: user_id,
  //     customer_id: customer_id,
  //     [column_name]: testEncrypted
  //   }

  //   try {
  //     const key = await this.dbService.add(tableName, dataPass).toPromise();
  //   } catch (error) {
  //     console.log(`Error adding item to ${tableName}:`, error);
  //   }
  // }

  public async processAEStoIDBwithCustomer1(
    tableName,
    res,
    user_id,
    column_name,
    customer_id,
    dynamicProperties: any = {}
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    // const existingEntry = entries.find(entry => entry.user_id === user_id && entry.customer_id === customer_id);
    const searchCriteria = { user_id, customer_id, ...dynamicProperties };

    const existingEntry = entries.find((entry) =>
      Object.keys(searchCriteria).every(
        (key) => entry[key] === searchCriteria[key]
      )
    );

    if (existingEntry) {
      await this.dbService.delete(tableName, existingEntry.id).toPromise();
    }

    if (res.length === 0) {
      return [];
    }

    const encryptedData = await this.cryptoService.encryptDataBased(
      JSON.stringify(res)
    );

    const dataPass = {
      user_id,
      customer_id,
      ...dynamicProperties,
      [column_name]: encryptedData,
    };

    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.error(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedAesEntriesWithCustomer(
    tableName,
    user_id,
    customer_id,
    column_name,
    dynamicProperties: any = {}
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    // const userEntry = entries.find(entry => entry.user_id === user_id && entry.customer_id === customer_id);
    const searchCriteria = { user_id, customer_id, ...dynamicProperties };

    //original working
    // const userEntry = entries.find((entry) =>
    //   Object.keys(searchCriteria).every(
    //     (key) => entry[key] === searchCriteria[key]
    //   )
    // );

    //handles if key is an array object
    const userEntry = entries.find((entry) =>
      Object.keys(searchCriteria).every((key) => {
        const entryValue = entry[key];
        const searchValue = searchCriteria[key];

        if (Array.isArray(entryValue)) {
          return entryValue.includes(searchValue);
        }

        return entryValue === searchValue;
      })
    );

    if (
      !userEntry ||
      !userEntry[column_name] ||
      userEntry[column_name].length === 0
    ) {
      return [];
    }

    const decryptedData = await this.cryptoService.decryptDataBased(
      userEntry[column_name]
    );

    return JSON.parse(decryptedData);
  }

  public async getDecryptedAesEntriesWithCustomerList(
    tableName: string,
    user_id: any,
    customer_id: any,
    column_name: string,
    dynamicProperties: any = {}
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    //for specific customer
    // const searchCriteria = { user_id, customer_id, ...dynamicProperties };

    //getting all matching data when customer id is 0
    const searchCriteria =
      customer_id === 0
        ? { user_id, ...dynamicProperties }
        : { user_id, customer_id, ...dynamicProperties };

    const matchingEntries = entries.filter((entry) =>
      Object.keys(searchCriteria).every(
        (key) => entry[key] === searchCriteria[key]
      )
    );

    if (
      matchingEntries.length === 0 ||
      !matchingEntries.every(
        (entry) => entry[column_name] && entry[column_name].length > 0
      )
    ) {
      return [];
    }
    const decryptedDataList = [];
    for (const entry of matchingEntries) {
      const decryptedData = await this.cryptoService.decryptDataBased(
        entry[column_name]
      );
      decryptedDataList.push(...JSON.parse(decryptedData));
    }

    return decryptedDataList;
  }

  public async processAESToIDBIncidental(tableName, res, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.id === user_id);
    let dataExisting = [];
    let toBePush = "";
    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
      let existingData = await this.cryptoService.decryptDataBased(
        existingEntry[column_name]
      );
      dataExisting = JSON.parse(existingData);
      dataExisting.push(res);
      toBePush = await this.cryptoService.encryptDataBased(
        JSON.stringify(dataExisting)
      );
    } else {
      dataExisting.push(res);
      toBePush = await this.cryptoService.encryptDataBased(
        JSON.stringify(dataExisting)
      );
    }

    const dataPass = {
      id: user_id,
      [column_name]: toBePush,
    };

    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async processToIdbProductsAes(tableName, res) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    if (tableName == "acp_products") {
      const matchingEntries = entries.filter((entry) => {
        const doctorId = entry[0].doctor_id;
        const acpDate = entry[0].acp_date;
        return doctorId == res.doctor_id && acpDate == res.acp_date;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    }

    if (tableName == "acp_products_planned") {
      const matchingEntries = entries.filter((entry) => {
        const id = entry[0].id;
        const acpDate = entry[0].acp_date;
        return id == res.id && acpDate == res.acp_date;
      });
      for (const entry of matchingEntries) {
        const entryId = entry.id;
        await this.dbService.delete(tableName, entryId).toPromise();
      }
    }
  }

  public async processAESToIDBwithFeaturedProduct(tableName, res, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find(entry => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }

    if (res.length == 0) {
      return []
    }

    const datapush = [];
    for (const item of res) {
      const dataToStore = {}
      const file = item.file
      const downloaded_file = JSON.stringify(item.downloaded_file)
      dataToStore['downloaded_file'] = await this.cryptoService.encryptDataBased(downloaded_file)
      dataToStore['file'] = {
        name: file.name,
        base64: await this.convertBlobToBase64(file),
        fileType: file.type
      }
      datapush.push(dataToStore)
    }

    const dataPass = {
      id: user_id,
      [column_name]: datapush,
    };

    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }
  public async getDecryptedAesEntriesWithFeaturedProduct(tableName, user_id, column_name) {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const existingEntry = entries.find(entry => entry.id === user_id);

      if (!existingEntry || !existingEntry[column_name]) {
        return [];
      }
      const decryptedData = [];
      for (const item of existingEntry[column_name]) {
        const itemToReturn = {}
        for (const [key, value] of Object.entries(item)) {
          if (key == 'file') {
            const imageValue = value as { base64: string; name: string; fileType: string };
            itemToReturn['file'] = this.base64ToFile(imageValue.base64, imageValue.name, imageValue.fileType);
          } else if (key == 'downloaded_file') {
            itemToReturn['downloaded_file'] = JSON.parse(await this.cryptoService.decryptDataBased(String(value)))
          }
        }
        decryptedData.push(itemToReturn)
      }

      return decryptedData;

    } catch (error) {
      console.log(`Error loading data from ${tableName}:`, error);
      return null;
    }
  }


  public async processAESToIDBwithFile(tableName, res, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find(entry => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }

    if (res.length == 0) {
      return []
    }

    const datapush = [];
    for (const item of res) {
      const dataToStore = {}
      const file = item.file
      const downloaded_file = JSON.stringify(item.downloaded_file)
      dataToStore['downloaded_file'] = await this.cryptoService.encryptDataBased(downloaded_file)
      dataToStore['file'] = {
        name: file.name,
        base64: await this.convertBlobToBase64(file),
        fileType: file.type
      }
      datapush.push(dataToStore)
    }

    const dataPass = {
      id: user_id,
      [column_name]: datapush,
    };

    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }
  public async getDecryptedAesEntriesWithFile(tableName, user_id, column_name) {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const existingEntry = entries.find(entry => entry.id === user_id);

      if (!existingEntry || !existingEntry[column_name]) {
        return [];
      }
      const decryptedData = [];
      for (const item of existingEntry[column_name]) {
        const itemToReturn = {}
        for (const [key, value] of Object.entries(item)) {
          if (key == 'file') {
            const imageValue = value as { base64: string; name: string; fileType: string };
            itemToReturn['file'] = this.base64ToFile(imageValue.base64, imageValue.name, imageValue.fileType);
          } else if (key == 'downloaded_file') {
            itemToReturn['downloaded_file'] = JSON.parse(await this.cryptoService.decryptDataBased(String(value)))
          }
        }
        decryptedData.push(itemToReturn)
      }

      return decryptedData;

    } catch (error) {
      console.log(`Error loading data from ${tableName}:`, error);
      return null;
    }
  }

  public async processAESToIDBwithSignDM(tableName, res, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find(entry => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }

    if (res.length == 0) {
      return []
    }

    const datapush = [];
    for (const item of res) {
      const dataToStore = {}
      const file = item.file
      const sign_details = JSON.stringify(item.sign_details)
      dataToStore['sign_details'] = await this.cryptoService.encryptDataBased(sign_details)
      dataToStore['file'] = {
        name: file.name,
        base64: await this.convertBlobToBase64(file),
        fileType: file.type
      }
      datapush.push(dataToStore)
    }

    const dataPass = {
      id: user_id,
      [column_name]: datapush,
    };

    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async getDecryptedAesEntriesWithSignDM(tableName, user_id, column_name) {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const existingEntry = entries.find(entry => entry.id === user_id);

      if (!existingEntry || !existingEntry[column_name]) {
        return [];
      }
      const decryptedData = [];
      for (const item of existingEntry[column_name]) {
        const itemToReturn = {}
        for (const [key, value] of Object.entries(item)) {
          if (key == 'file') {
            const imageValue = value as { base64: string; name: string; fileType: string };
            itemToReturn['file'] = this.base64ToFile(imageValue.base64, imageValue.name, imageValue.fileType);
          } else if (key == 'sign_details') {
            itemToReturn['sign_details'] = JSON.parse(await this.cryptoService.decryptDataBased(String(value)))
          }
        }
        decryptedData.push(itemToReturn)
      }

      return decryptedData;

    } catch (error) {
      console.log(`Error loading data from ${tableName}:`, error);
      return null;
    }
  }

  //  NGSW STORE VERSION

  public async storeVersion(tableName, res) {
    const dataPass = {
      id: 1,
      version: res,
    };

    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
    } catch (error) {
      console.log(`Error adding item to ${tableName}:`, error);
    }
  }

  public async updateVersion(tableName: string, res: string) {
    const dataPass = {
      id: 1,
      version: res,
    };

    try {
      const key = await this.dbService.update(tableName, dataPass).toPromise();
      console.log(`Version updated for ${tableName} with key:`, key);
    } catch (error) {
      console.log(`Error updating version for ${tableName}:`, error);
    }
  }

  public async getStoredVersion(): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
      this.dbService.getByID("ngsw_version", 1).subscribe({
        next: (result) => {
          resolve(result ? (result as any).version : undefined);
        },
        error: (error) => {
          console.error("Error fetching stored version:", error);
          reject(error);
        },
      });
    });
  }

  // Other Stuff

  public async clearTable(tableName: string) {
    try {
      await this.dbService.clear(tableName).toPromise();
      console.log(`Table "${tableName}" cleared successfully.`);
    } catch (error) {
      if (error.name === "NotFoundError") {
        console.warn(`Table "${tableName}" does not exist, skipping.`);
      } else {
        console.error(`Error clearing "${tableName}" table:`, error);
      }
    }

  }

  public compareSettings(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) return false;

    const compareObjects = (obj1: any, obj2: any): boolean => {
      if (
        typeof obj1 !== "object" ||
        obj1 === null ||
        typeof obj2 !== "object" ||
        obj2 === null
      ) {
        return obj1 == obj2;
      }

      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) return false;

      for (const key of keys1) {
        const value1 = obj1[key];
        const value2 = obj2[key];
        if (!compareObjects(value1, value2)) {
          return false; //
        }
      }
      return true;
    };

    const sortedArr1 = [...arr1].sort((a, b) =>
      JSON.stringify(a).localeCompare(JSON.stringify(b))
    );
    const sortedArr2 = [...arr2].sort((a, b) =>
      JSON.stringify(a).localeCompare(JSON.stringify(b))
    );

    for (let i = 0; i < sortedArr1.length; i++) {
      if (!compareObjects(sortedArr1[i], sortedArr2[i])) {
        return false;
      }
    }

    return true;
  }

  public compareObjects(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  public async deleteUserEntry(tableName, user_id) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }
  }

  public async GetDate() {
    const currentDate = new Date();
    const utcPlus8Date = new Date(currentDate.getTime() + 8 * 60 * 60 * 1000);
    return utcPlus8Date.toISOString().split("T")[0];
  }

  public getCurrentDateSQLFormat() {
    const currentDate = new Date();

    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  public getFromLocalStorage(key: string): any {
    return JSON.parse(localStorage.getItem(key) || "{}");
  }

  //nested arrays
  public compareSoReasons(obj1: any, obj2: any): boolean {
    const compareObjects = (innerObj1: any, innerObj2: any): boolean => {
      if (
        typeof innerObj1 !== "object" ||
        innerObj1 === null ||
        typeof innerObj2 !== "object" ||
        innerObj2 === null
      ) {
        return innerObj1 == innerObj2;
      }

      const keys1 = Object.keys(innerObj1);
      const keys2 = Object.keys(innerObj2);

      if (keys1.length !== keys2.length) return false;

      for (const key of keys1) {
        const value1 = innerObj1[key];
        const value2 = innerObj2[key];
        if (!compareObjects(value1, value2)) {
          return false;
        }
      }
      return true;
    };

    const sortedKeys1 = Object.keys(obj1).sort();
    const sortedKeys2 = Object.keys(obj2).sort();

    if (sortedKeys1.length !== sortedKeys2.length) return false;

    for (let i = 0; i < sortedKeys1.length; i++) {
      if (!compareObjects(obj1[sortedKeys1[i]], obj2[sortedKeys2[i]])) {
        return false;
      }
    }

    return true;
  }

  public async deleteUserEntryTest(tableName, user_id) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.user_id === user_id);
    if (existingEntry) {
      await this.dbService.delete(tableName, existingEntry.id).toPromise();
    }
  }

  public async deleteUserEntryTestCustom(tableName, user_id, ref_number) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = entries.find((entry) => entry.user_id === user_id && entry.so_reference === ref_number);
    console.log("existing delete ", existingEntry);
    if (existingEntry) {
      await this.dbService.delete(tableName, existingEntry.id).toPromise();
    }
  }

  public async deleteUserEntryTestCustomDynamic(
    tableName: string,
    user_id: any,
    dynamicProperties: any = {}
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    const searchCriteria = { user_id, ...dynamicProperties };

    const existingEntry = entries.find((entry) =>
      Object.keys(searchCriteria).every((key) => {
        const entryValue = entry[key];
        const searchValue = searchCriteria[key];

        if (Array.isArray(entryValue)) {
          return entryValue.includes(searchValue);
        }

        return entryValue === searchValue;
      })
    );

    if (existingEntry) {
      await this.dbService.delete(tableName, existingEntry.id).toPromise();
    }
  }


  public async processAEStoIDBwithCustomer1File(
    tableName,
    file: File,
    user_id,
    column_name,
    customer_id,
    dynamicProperties: any = {}
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    const searchCriteria = { user_id, customer_id, ...dynamicProperties };

    const existingEntry = entries.find((entry) =>
      Object.keys(searchCriteria).every(
        (key) => entry[key] === searchCriteria[key]
      )
    );

    if (existingEntry) {
      await this.dbService.delete(tableName, existingEntry.id).toPromise();
    }

    if (!file) {
      console.error("No file provided");
      return [];
    }

    const fileReader = new FileReader();
    const fileData: Promise<ArrayBuffer> = new Promise((resolve, reject) => {
      fileReader.onload = () => resolve(fileReader.result as ArrayBuffer);
      fileReader.onerror = () => reject(fileReader.error);
      fileReader.readAsArrayBuffer(file);
    });

    let encryptedFileData: string;
    try {
      const fileContent = await fileData;

      const base64String = btoa(
        String.fromCharCode(...new Uint8Array(fileContent))
      );

      encryptedFileData = await this.cryptoService.encryptDataBased(base64String);
    } catch (error) {
      console.error("Error reading or encrypting file:", error);
      return [];
    }

    const dataPass = {
      user_id,
      customer_id,
      ...dynamicProperties,
      [column_name]: encryptedFileData,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
    };

    try {
      const key = await this.dbService.add(tableName, dataPass).toPromise();
      console.log(`File successfully saved with key: ${key}`);
    } catch (error) {
      console.error(`Error adding file to ${tableName}:`, error);
    }
  }

  public async getDecryptedAesEntriesWithCustomerSingleFile(
    tableName,
    user_id,
    customer_id,
    column_name,
    dynamicProperties: any = {}
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    const searchCriteria = { user_id, customer_id, ...dynamicProperties };

    const userEntry = entries.find((entry) =>
      Object.keys(searchCriteria).every(
        (key) => entry[key] === searchCriteria[key]
      )
    );

    if (
      !userEntry ||
      !userEntry[column_name] ||
      userEntry[column_name].length === 0
    ) {
      console.error("No matching entry found or file data is missing");
      return null;
    }

    try {
      const decryptedBase64 = await this.cryptoService.decryptDataBased(
        userEntry[column_name]
      );

      const binaryString = atob(decryptedBase64);
      const len = binaryString.length;
      const arrayBuffer = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        arrayBuffer[i] = binaryString.charCodeAt(i);
      }

      const fileBlob = new Blob([arrayBuffer], {
        type: userEntry.file_type || "application/octet-stream",
      });

      return {
        fileBlob,
        fileName: userEntry.file_name,
        fileType: userEntry.file_type,
        fileSize: userEntry.file_size,
      };
    } catch (error) {
      console.error("Error decrypting or processing file data:", error);
      return null;
    }
  }

  public async getDecryptedAesEntriesWithCustomerMultipleFiles(
    tableName,
    user_id,
    customer_id,
    column_name,
    dynamicProperties: any = {}
  ) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();

    // const searchCriteria = { user_id, customer_id, ...dynamicProperties };
    const searchCriteria =
      customer_id === 0
        ? { user_id, ...dynamicProperties }
        : { user_id, customer_id, ...dynamicProperties };

    // Find all entries matching the criteria
    const matchingEntries = entries.filter((entry) =>
      Object.keys(searchCriteria).every(
        (key) => entry[key] === searchCriteria[key]
      )
    );

    if (!matchingEntries || matchingEntries.length === 0) {
      console.error("No matching entries found");
      return [];
    }

    const decryptedFiles = [];

    for (const userEntry of matchingEntries) {
      try {
        if (!userEntry[column_name] || userEntry[column_name].length === 0) {
          console.warn(`Entry with ID ${userEntry.id} has no file data`);
          continue;
        }

        // Decrypt the Base64 encoded file data
        const decryptedBase64 = await this.cryptoService.decryptDataBased(
          userEntry[column_name]
        );

        // Convert Base64 back to an ArrayBuffer
        const binaryString = atob(decryptedBase64);
        const len = binaryString.length;
        const arrayBuffer = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          arrayBuffer[i] = binaryString.charCodeAt(i);
        }

        // Create a Blob from the ArrayBuffer
        const fileBlob = new Blob([arrayBuffer], {
          type: userEntry.file_type || "application/octet-stream",
        });

        // Add the file blob and metadata to the result
        decryptedFiles.push({
          fileBlob,
          fileName: userEntry.file_name,
          fileType: userEntry.file_type,
          fileSize: userEntry.file_size,
        });
      } catch (error) {
        console.error(
          `Error decrypting or processing file data for entry ID ${userEntry.id}:`,
          error
        );
      }
    }

    return decryptedFiles;
  }

  // HTML FILES
  public async saveToIndexedDB(zipId: string, files: { [key: string]: string | ArrayBuffer }) {
    Object.entries(files).forEach(([path, content]) => {
      this.dbService.add('htmlFiles', { zipId, path, content }).subscribe({
        next: () => console.log(`File ${path} saved successfully`),
        error: (err) => console.error(`Error saving file ${path}:`, err),
      });
    });
  }

  public async fetchFilesFromIndexedDB(zipId: string): Promise<{ [key: string]: string }> {
    const files: { [key: string]: string } = {};
    const allFiles = await this.dbService.getAllByIndex('htmlFiles', 'zipId', IDBKeyRange.only(zipId)).toPromise();

    allFiles.forEach((file: any) => {
      files[file.path] = file.content;
    });

    return files;
  }

  public emptyArrayToString(theArray: any) {
    if (Array.isArray(theArray)) {
      if (theArray.length == 0) {
        return ''
      }
    } else {
      return theArray
    }
  }

  public async processAESToIDBwithFileObject(tableName, entries, user_id, column_name) {
    const existingEntries: any[] = await this.dbService.getAll(tableName).toPromise();
    const existingEntry = existingEntries.find((entry) => entry.id === user_id);

    if (existingEntry) {
      await this.dbService.delete(tableName, user_id).toPromise();
    }

    if (entries.length === 0) {
      return [];
    }

    const serializedEntries = await Promise.all(entries.map(async (entry: any) => {
      const updatedSurveys = await Promise.all(entry.surveys.map(async (survey: any) => {
        let updatedImages = {};
        if (survey.formImages instanceof Map) {
          for (const [key, value] of survey.formImages.entries()) {
            let newValue = { ...value };
            if (value.file && value.file instanceof File) {
              const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.readAsDataURL(value.file);
              });

              newValue = {
                ...value,
                file: {
                  name: value.file.name,
                  size: value.file.size,
                  type: value.file.type,
                  base64,
                },
              };
            }
            updatedImages[key] = newValue;
          }
        }
        return { ...survey, formImages: updatedImages, };
      }));
      return { ...entry, surveys: updatedSurveys, };
    }));

    const serializedData = JSON.stringify(serializedEntries);
    const encryptedData = await this.cryptoService.encryptDataBased(serializedData);

    const dataToSave = {
      id: user_id,
      [column_name]: encryptedData,
    };

    try {
      await this.dbService.add(tableName, dataToSave).toPromise();
    } catch (error) {
      console.log("Error adding item to ${tableName}:, error");
    }
  }

  public async getDecryptedAesEntriesWithFileObject(tableName, user_id, column_name) {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const userEntriesMap = new Map(entries.map((entry) => [entry.id, entry]));
    const userEntry = userEntriesMap.get(user_id);

    if (!userEntry || !userEntry[column_name] || userEntry[column_name].length <= 0) {
      return [];
    }

    const decryptedData = await this.cryptoService.decryptDataBased(userEntry[column_name]);
    const parsedData = JSON.parse(decryptedData);

    const restoredEntries = await Promise.all(parsedData.map(async (entry: any) => {
      const restoredSurveys = await Promise.all(entry.surveys.map(async (survey: any) => {
        const formImagesObj = survey.formImages;
        const map = new Map();

        if (formImagesObj && typeof formImagesObj === 'object') {
          for (const key of Object.keys(formImagesObj)) {
            const value = formImagesObj[key];
            const fileMeta = value.file;

            const file = await fetch(fileMeta.base64)
              .then(res => res.blob())
              .then(blob => new File([blob], fileMeta.name, { type: fileMeta.type }));

            map.set(key, { ...value, file });
          }
        }
        return { ...survey, formImages: map };
      }));
      return { ...entry, surveys: restoredSurveys };
    }));
    return restoredEntries;
  }

  public async deleteNestedEncryptedObject(
    tableName: string,
    user_id: any,
    columnName: string,
    objectToDelete: Record<string, any>
  ): Promise<void> {
    const entries: any[] = await this.dbService.getAll(tableName).toPromise();
    const userEntry = entries.find((entry) => entry.id === user_id);

    if (!userEntry || !userEntry[columnName]) return;

    const decryptedJson = await this.cryptoService.decryptDataBased(userEntry[columnName]);
    let nestedArray = JSON.parse(decryptedJson);

    const [key, value] = Object.entries(objectToDelete)[0];

    nestedArray = nestedArray.filter((item: any) => item[key] !== value);

    const updatedEncryptedData = await this.cryptoService.encryptDataBased(
      JSON.stringify(nestedArray)
    );

    userEntry[columnName] = updatedEncryptedData;
    await this.dbService.update(tableName, userEntry).toPromise();
  }

  public async deleteNestedEncryptedObjectv2(
    tableName: string,
    user_id: any,
    columnName: string,
    objectToDelete: Record<string, any>
  ): Promise<void> {
    try {
      const entries: any[] = await this.dbService.getAll(tableName).toPromise();
      const userEntry = entries.find((entry) => entry.id === user_id); // match by 'id'

      if (!userEntry) {
        console.warn(`[IndexedDB] No entry found with id ${user_id} in ${tableName}`);
        return;
      }

      if (!userEntry[columnName]) {
        console.warn(`[IndexedDB] Column "${columnName}" not found in entry`);
        return;
      }

      let decryptedJson: string;
      try {
        decryptedJson = await this.cryptoService.decryptDataBased(userEntry[columnName]);
      } catch (err) {
        console.error(`[Crypto] Failed to decrypt data in "${columnName}"`, err);
        return;
      }

      let nestedArray: any[];
      try {
        nestedArray = JSON.parse(decryptedJson);
      } catch (err) {
        console.error(`[Parse] Failed to parse decrypted JSON in "${columnName}"`, err);
        return;
      }

      const matchConditions = Object.entries(objectToDelete);
      if (matchConditions.length === 0) {
        console.warn(`[Input] Invalid objectToDelete — no key/value pair provided`);
        return;
      }

      const originalLength = nestedArray.length;
      nestedArray = nestedArray.filter((item: any) => {
        return !matchConditions.every(([key, value]) => item[key] === value);
      });

      if (nestedArray.length === originalLength) {
        console.warn(`[Filter] No matching item found in "${columnName}" for deletion`);
        return;
      }

      const updatedEncryptedData = await this.cryptoService.encryptDataBased(
        JSON.stringify(nestedArray)
      );

      userEntry[columnName] = updatedEncryptedData;
      await this.dbService.update(tableName, userEntry).toPromise();

    } catch (err) {
      console.error(`[Error] Unexpected error during deletion:`, err);
    }
  }

}


